import { useRef, type ChangeEvent, type FocusEvent } from 'react';

import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import MastercardIcon from '@liquidweb-dev/undercurrent/icons/platform/Mastercard';
import VisaIcon from '@liquidweb-dev/undercurrent/icons/platform/Visa';
import InputAdornment from '@liquidweb-dev/undercurrent/InputAdornment';
import AmericanExpressLogo from '@liquidweb-dev/undercurrent/logos/AmericanExpress';
import DiscoverLogo from '@liquidweb-dev/undercurrent/logos/Discover';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import TextField, {
	type TextFieldProps,
} from '@liquidweb-dev/undercurrent/TextField';

import { getIssuer } from 'modules/creditCard';
import { useInput } from 'components/LWForm/LWForm';
import calculateNewCursorPosition from 'utility/calculateNewCursorPosition';
import clean from 'utility/cleanDigits';

function formatCreditCardNumber(value: string, cursorPosition: number | null) {
	if (!value) {
		return { formattedValue: value, newCursorPosition: cursorPosition };
	}
	const cleanValue = clean(value);

	if (cleanValue.length - 1 === 16 && cursorPosition) {
		return {
			formattedValue:
				value.slice(0, cursorPosition - 1) + value.slice(cursorPosition),
			newCursorPosition: value.length,
		};
	}

	const issuer = getIssuer(value);
	const groups = issuer === 'amex' ? [4, 6, 5] : [4, 4, 4, 4];

	const formattedValue = groups.reduce<[string, number]>(
		([acc, currentPosition], group, index) => {
			if (cleanValue.length > currentPosition) {
				const segment = cleanValue.slice(
					currentPosition,
					currentPosition + group,
				);
				return [
					`${acc}${index > 0 ? ' ' : ''}${segment}`,
					currentPosition + group,
				];
			}
			return [acc, currentPosition];
		},
		['', 0],
	)[0];

	const newCursorPosition = calculateNewCursorPosition(
		value,
		formattedValue,
		cursorPosition,
	);

	return { formattedValue, newCursorPosition };
}

const AcceptedCardWrapper = (props: Omit<BoxProps, 'sx'>) => (
	<Box
		sx={({ vars }) => ({
			border: '1px solid',
			borderColor: vars.palette.uc.border.weak,
			borderRadius: '4px',
			height: '20px',
			width: '30px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		})}
		{...props}
	/>
);

const Visa = () => (
	<AcceptedCardWrapper>
		<VisaIcon />
	</AcceptedCardWrapper>
);

const Mastercard = () => (
	<AcceptedCardWrapper>
		<MastercardIcon />
	</AcceptedCardWrapper>
);

const Discover = () => <DiscoverLogo sx={{ height: '20px', width: '29px' }} />;

const AmericanExpress = () => (
	<AmericanExpressLogo sx={{ height: '20px', width: '29px' }} />
);

const AcceptedCards = ({
	selectedIssuer,
}: {
	selectedIssuer: string | null;
}) => {
	if (selectedIssuer === 'visa') {
		return <Visa />;
	}

	if (selectedIssuer === 'mastercard') {
		return <Mastercard />;
	}

	if (selectedIssuer === 'discover') {
		return <Discover />;
	}

	if (selectedIssuer === 'amex') {
		return <AmericanExpress />;
	}

	return (
		<Stack
			sx={{
				flexDirection: 'row',
				gap: 50,
				alignItems: 'center',
				opacity: '0.5',
			}}
		>
			<Visa />
			<Mastercard />
			<Discover />
			<AmericanExpress />
		</Stack>
	);
};

export type HookedTextFieldCreditCardProps = Omit<TextFieldProps, 'select'>;

const HookedTextFieldCreditCard = ({
	error: errorProp,
	helperText,
	name,
	onBlur,
	onChange,
	...rest
}: HookedTextFieldCreditCardProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const { value, onValueChange, error, validate } = useInput(name!);

	if (!name) {
		return null;
	}

	const selectedIssuer = getIssuer(value);

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (!event.target) {
			return;
		}

		const { value: newValue, selectionStart } = event.target;

		const { formattedValue, newCursorPosition } = formatCreditCardNumber(
			newValue,
			selectionStart,
		);

		onValueChange(formattedValue);
		setTimeout(() => {
			if (inputRef.current) {
				inputRef.current.setSelectionRange(
					newCursorPosition,
					newCursorPosition,
				);
			}
		}, 0);

		if (onChange) {
			onChange(event);
		}

		// validate on input change only if there is an existing error
		if (!error) {
			return;
		}

		validate(event.target);
	};

	const handleBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
		if (!event.target) {
			return;
		}

		if (onBlur) {
			onBlur(event);
		}

		validate(event.target);
	};

	return (
		<TextField
			autoComplete="cc-number"
			error={Boolean(error || errorProp)}
			helperText={error || errorProp || helperText}
			label="Card number"
			name={name}
			onBlur={handleBlur}
			onChange={handleChange}
			placeholder="1234 1234 1234 1234"
			slotProps={{
				htmlInput: { ref: inputRef },
				input: {
					endAdornment: (
						<InputAdornment position="end">
							<AcceptedCards selectedIssuer={selectedIssuer} />
						</InputAdornment>
					),
				},
			}}
			{...rest}
			value={value}
		/>
	);
};

export default HookedTextFieldCreditCard;
